'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import io, { Socket } from 'socket.io-client';

/* -------------------- helpers -------------------- */
const getAccessTokenFromCookie = (): string | undefined => {
  if (typeof document === 'undefined') return undefined;
  return document.cookie.split('; ').find(row => row.startsWith('access-token='))?.slice('access-token='.length);
};
interface SocketUser {
  id: string;
  name: string;
  email: string;
  shortName: string;
}
interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  listenToModuleConnection: (moduleName: string, userName: string, setSocketUserList: (users: SocketUser[]) => void) => void;
  leaveModule: (moduleName: string, user: SocketUser) => void;
  leaveAllModules: (user: SocketUser) => void;
  joinModule: (moduleName: string, user: SocketUser) => void;
  getUsersInRoom: (moduleName: string, userName: string, setSocketUserList: (users: SocketUser[]) => void) => void;
  reconnectWithNewToken: () => void;
  swapRoleChannels: (leavingChannels: string[], joiningChannels: string[], user: SocketUser) => void;
}
interface ModuleListeners {
  [moduleId: string]: {
    userName: string;
    callback: (users: SocketUser[]) => void;
  }[];
}
const SocketContext = createContext<SocketContextType | null>(null);
const notifyModuleListeners = (listeners: {
  userName: string;
  callback: (users: SocketUser[]) => void;
}[], users: SocketUser[]) => {
  listeners.forEach(({
    userName,
    callback
  }) => {
    const filteredUsers = users.filter(user => user.name && user.name.trim() !== userName.trim());
    callback(filteredUsers);
  });
};
interface SocketProviderProps {
  children: ReactNode;
}
export const SocketProvider = ({
  children
}: SocketProviderProps) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const socketUrl = process.env.NEXT_PUBLIC_SOCKET_BACKEND_URL;
  const socketRef = useRef<Socket | null>(null);
  const listenersSetupRef = useRef<boolean>(false);
  const moduleListenersRef = useRef<ModuleListeners>({});
  const pendingJoinsRef = useRef<Array<{
    moduleName: string;
    user: SocketUser;
  }>>([]);
  const activeModulesRef = useRef<Array<{
    moduleName: string;
    user: SocketUser;
  }>>([]);
  // Ref mirror of isConnected — lets callbacks read current value without being in deps array
  const isConnectedRef = useRef<boolean>(false);
  const reconnectWithNewToken = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      const token = getAccessTokenFromCookie();
      socketRef.current.auth = {
        token
      };
      socketRef.current.connect();
    }
  }, []);

  // Swap role-specific socket rooms during impersonation start/end.
  // Explicitly leaves old channels (emits leaveModule to server), registers new channels
  // so the connect handler joins them after token refresh, then reconnects with the new token.
  const swapRoleChannels = useCallback((leavingChannels: string[], joiningChannels: string[], user: SocketUser): void => {
    if (!socketRef.current) return;
    leavingChannels.forEach(channel => {
      activeModulesRef.current = activeModulesRef.current.filter(j => j.moduleName !== channel);
      if (isConnectedRef.current) {
        socketRef.current!.emit('leaveModule', {
          moduleName: channel,
          user
        });
      }
    });
    joiningChannels.forEach(channel => {
      if (!activeModulesRef.current.some(j => j.moduleName === channel)) {
        activeModulesRef.current.push({
          moduleName: channel,
          user
        });
      }
    });
    socketRef.current.disconnect();
    const token = getAccessTokenFromCookie();
    socketRef.current.auth = {
      token
    };
    socketRef.current.connect();
  }, []);
  useEffect(() => {
    if (!socketUrl) {
      return () => {};
    }
    if (!socketRef.current) {
      const token = getAccessTokenFromCookie();
      socketRef.current = io(socketUrl, {
        transports: ['websocket'],
        auth: {
          token
        },
        withCredentials: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 200000
      });
    }
    if (!listenersSetupRef.current && socketRef.current) {
      const currentSocket = socketRef.current;
      currentSocket.on('connect', () => {
        isConnectedRef.current = true;
        setIsConnected(true);
        // Re-join active rooms before React re-renders (covers server restart scenario)
        activeModulesRef.current.forEach(({
          moduleName,
          user
        }) => {
          currentSocket.emit('joinModule', {
            moduleName,
            user
          });
        });
        // Replay joins queued while disconnected
        pendingJoinsRef.current.forEach(({
          moduleName,
          user
        }) => {
          currentSocket.emit('joinModule', {
            moduleName,
            user
          });
        });
        pendingJoinsRef.current = [];
      });
      currentSocket.on('disconnect', _reason => {
        isConnectedRef.current = false;
        setIsConnected(false);
      });
      currentSocket.on('connect_error', _error => {
        isConnectedRef.current = false;
        setIsConnected(false);
      });
      currentSocket.on('reconnect', () => {
        isConnectedRef.current = true;
        setIsConnected(true);
        // Re-request room state for all active modules after reconnect
        Object.entries(moduleListenersRef.current).forEach(([mod, listeners]) => {
          if (listeners.length > 0) {
            currentSocket.emit('getUsersInRoom', mod);
          }
        });
      });
      currentSocket.on('moduleStatus', ({
        module,
        users
      }) => {
        if (moduleListenersRef.current[module]) {
          notifyModuleListeners(moduleListenersRef.current[module], users);
        }
      });
      listenersSetupRef.current = true;
    }
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        listenersSetupRef.current = false;
      }
    };
  }, [socketUrl]);
  const listenToModuleConnection = useCallback((moduleName: string, userName: string, setSocketUserList: (users: SocketUser[]) => void): void => {
    if (!socketRef.current) return;
    if (!moduleListenersRef.current[moduleName]) {
      moduleListenersRef.current[moduleName] = [];
    }
    const existingIndex = moduleListenersRef.current[moduleName].findIndex(listener => listener.callback === setSocketUserList);
    if (existingIndex === -1) {
      moduleListenersRef.current[moduleName].push({
        userName,
        callback: setSocketUserList
      });
    } else {
      moduleListenersRef.current[moduleName][existingIndex].userName = userName;
    }
  }, []);
  const leaveModule = useCallback((moduleName: string, user: SocketUser): void => {
    activeModulesRef.current = activeModulesRef.current.filter(j => !(j.moduleName === moduleName && j.user.id === user.id));
    if (socketRef.current && isConnectedRef.current) {
      socketRef.current.emit('leaveModule', {
        moduleName,
        user
      });
      if (moduleListenersRef.current[moduleName]) {
        moduleListenersRef.current[moduleName] = moduleListenersRef.current[moduleName].filter(listener => listener.userName !== user.name);
      }
    }
  }, []);
  const leaveAllModules = useCallback((user: SocketUser): void => {
    activeModulesRef.current = [];
    if (socketRef.current && isConnectedRef.current) {
      socketRef.current.emit('leaveAllModules', {
        user
      });
      Object.keys(moduleListenersRef.current).forEach(moduleName => {
        moduleListenersRef.current[moduleName] = moduleListenersRef.current[moduleName].filter(listener => listener.userName !== user.name);
      });
      socketRef.current.disconnect();
      isConnectedRef.current = false;
      setIsConnected(false);
    }
  }, []);
  const joinModule = useCallback((moduleName: string, user: SocketUser): void => {
    // Track for reconnect regardless of connected state
    if (!activeModulesRef.current.some(j => j.moduleName === moduleName && j.user.id === user.id)) {
      activeModulesRef.current.push({
        moduleName,
        user
      });
    }
    if (socketRef.current && isConnectedRef.current) {
      socketRef.current.emit('joinModule', {
        moduleName,
        user
      });
    } else {
      pendingJoinsRef.current.push({
        moduleName,
        user
      });
    }
  }, []);
  const getUsersInRoom = useCallback((moduleName: string, userName: string, setSocketUserList: (users: SocketUser[]) => void): void => {
    if (!socketRef.current) return;
    listenToModuleConnection(moduleName, userName, setSocketUserList);
    if (isConnectedRef.current) {
      socketRef.current.emit('getUsersInRoom', moduleName);
    }
    // Disconnected case: reconnect event handler re-emits for all active modules
  }, [listenToModuleConnection]);
  const contextValue = useMemo(() => ({
    socket: socketRef.current,
    isConnected,
    listenToModuleConnection,
    leaveModule,
    leaveAllModules,
    joinModule,
    getUsersInRoom,
    reconnectWithNewToken,
    swapRoleChannels
  }), [isConnected, listenToModuleConnection, leaveModule, leaveAllModules, joinModule, getUsersInRoom, reconnectWithNewToken, swapRoleChannels]);
  return <SocketContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="SocketProvider" data-sentry-source-file="socketContext.tsx">{children}</SocketContext.Provider>;
};
export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};